import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../../../services/api";
import qs from "qs";
import { AgGridReact } from "ag-grid-react";
import 'ag-grid-enterprise';
import StatusTooltip from "../tooltips/StatusTooltip";
import SetupPanel from "./panels/SetupPanel";
import useReport from "./contexts/ReportContext";
import Loader from "../../../loaders";
import { StatusPanelLeft, StatusPanelRight } from "../panels";
import moment from "moment";

const editableCellClass = "roster-cell";

const getContentCellClass = (content) => {
    if (!content) {
        return "grid-empty-roster";
    }
    return "";
}

export default function RosterAccommodationReport(props) {
    const {
        dispatchUnit,
        startDate,
        endDate,
        trimStartDate,
        trimEndDate,
        project,
        setDataLoaded,
        dataLoaded,
        setStartDate,
        setEndDate,
    } = useReport();

    const gridRef = useRef();
    const [rowData, setRowData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [disableTrimStartDateDefault, setDisableTrimStartDateDefault] = useState(null);
    const [disableTrimEndDateDefault, setDisableTrimEndDateDefault] = useState(null);
    const [columnDefs, setColumnDefs] = useState([]);

    const loadingOverlayComponent = useMemo(() => Loader, []);

    const getRowId = useMemo(() => (params) => params?.data?.rosterCandidateId, []);

    const statusBar = useMemo(() => ({
        statusPanels: [
            {
                statusPanel: StatusPanelLeft,
                align: 'left',
            },
            {
                statusPanel: StatusPanelRight,
                align: 'right',
            },
        ],
    }), []);

    useEffect(() => {
        if (process.env.REACT_APP_HRIS_ROSTER_ACCOMMODATION_REPORT_GRID_COLUMN_STATE in localStorage && !!gridRef.current.columnApi) {
            gridRef.current.columnApi.applyColumnState({
                state: JSON.parse(localStorage.getItem(process.env.REACT_APP_HRIS_ROSTER_ACCOMMODATION_REPORT_GRID_COLUMN_STATE)),
                applyOrder: true,
                defaultState: {
                    aggFunc: null,
                    flex: null,
                    hide: false,
                    pinned: null,
                    pivot: false,
                    pivotIndex: null,
                    rowGroup: false,
                    rowGroupIndex: null,
                    sort: null,
                    sortIndex: null
                }
            });
        }
        setDataLoaded(rowData && rowData.length > 0)
    }, [rowData]);

    const defaultColDef = useMemo(() => ({
        cellClass: 'roster-cell',
        autoHeight: false,
        resizable: true,
        filter: false,
        suppressPaste: true,
        suppressFillHandle: true,
        editable: false,
        suppressMenu: true,
        sortable: false,
        suppressPaste: true,
        suppressFillHandle: true,
        suppressKeyboardEvent: true,
    }), []);

    const staticColumnDefs = useMemo(() => {
        return [
            {
                headerName: "",
                children: [
                    {
                        field: "slot",
                        headerName: "Slot",
                        width: 55,
                        pinned: true,
                        sort: "asc",
                    },
                    {
                        field: "candidateName",
                        headerName: "Employee Name",
                        width: 150,
                        pinned: true,
                        cellClass: (params) => {
                            const contentCellClass = getContentCellClass(params.data?.candidateName);
                            return `${editableCellClass} ${contentCellClass}`;
                        },
                        filter: 'agTextColumnFilter',
                    },
                    {
                        field: "positionName",
                        headerName: "Employed Position",
                        width: 200,
                        columnGroupShow: 'closed',
                        cellClass: (params) => {
                            var contentCellClass = getContentCellClass(params.data?.positionName);
                            return `roster-cell ${contentCellClass}`;
                        },
                        pinned: true,
                    },
                    {
                        field: "shiftName",
                        headerName: "Shift",
                        width: 70,
                        cellClass: (params) => {
                            var contentCellClass = getContentCellClass(params.data?.shiftName);
                            return `${editableCellClass} ${contentCellClass}`;
                        },
                        pinned: true,
                    },
                    {
                        field: "siteTravelTypeName",
                        headerName: "Site Travel Type",
                        width: 100,
                        cellClass: (params) => {
                            var contentCellClass = getContentCellClass(params.data?.siteTravelTypeName);
                            return `${editableCellClass} ${contentCellClass}`;
                        },
                        pinned: true,
                    },
                    {
                        field: "pointOfHireName",
                        headerName: "Point of Hire",
                        width: 90,
                        cellClass: (params) => {
                            var contentCellClass = getContentCellClass(params.data?.pointOfHireName);
                            return `${editableCellClass} ${contentCellClass}`;
                        },
                        pinned: true,
                    },
                    {
                        field: "rosterCandidateCompanyName",
                        headerName: "Entity",
                        width: 150,
                        cellClass: (params) => {
                            if (params.data.rosterCompanyId !== params.data.rosterCandidateCompanyId) {
                                return `roster-cell text-danger`;
                            }
                            return 'roster-cell'
                        },
                    },
                ]
            },
        ]
    }, []);

    const loadSchedule = useCallback(query => {
        gridRef.current.api.showLoadingOverlay();
        gridRef.current.api.clearRangeSelection();

        const queryString = qs.stringify(query ? {...query, selectedScheduleTypes: "W"} : {selectedScheduleTypes: "W"}, { allowDots: true });

        api.get(`/rostergrid?${queryString}`)
            .then((response) => {
                try {
                    const {
                        schedules,
                        filterStartDate,
                        filterEndDate,
                        dispatchUnit
                    } = response.data;

                    // console.log({
                    //     schedules,
                    //     filterStartDate,
                    //     filterEndDate,
                    //     dispatchUnit,
                    // });

                    if (schedules && schedules.length == 0) {
                        setColumnDefs([...staticColumnDefs]);
                        setRowData([]);
                        setLoading(false);
                        return;
                    }

                    const dateColumns = [];

                    const start = moment(filterStartDate, 'YYYY-MM-DD');
                    const end = moment(filterEndDate, 'YYYY-MM-DD');
                    setStartDate(new Date(start.year(), start.month(), start.date()));
                    setEndDate(new Date(end.year(), end.month(), end.date()));
                    //setStartDate(new Date(start.year(), '01', '01'));
                    //setEndDate(new Date(end.year(), '01', '01'));

                    const current = moment(start);

                    while (current <= end) {
                        dateColumns.push(moment(current));
                        current.add(1, 'd');
                    }

                    setColumnDefs([
                        ...staticColumnDefs,
                        ...dateColumns.map((m) => ({
                            headerName: m.format('ddd'),
                            marryChildren: true,
                            children: [
                                {
                                    field: `dates.${m.format('YYYYMMDD')}`,
                                    // tooltipField: `dates.${m.format('YYYYMMDD')}`,
                                    headerName: m.format('DD/MM/YYYY'),
                                    autoHeight: false,
                                    resizable: false,
                                    width: 30,
                                    headerClass: 'date-header',
                                    suppressMovable: true,
                                    lockPosition: 'right',
                                    filter: false,
                                    suppressMenu: true,
                                    valueFormatter: (params) => {
                                        // console.log("value formatter", {params});
                                        return params?.value?.value === "W" ? "1" : "";
                                    },
                                    cellClass: (params) => {
                                        // console.log("cell class", {params});
                                        return params.value && params.value.value ? `date-cell roster-report-icon ${params.value?.className} amount amount${params.value?.value.length}` : 'date-cell';
                                    },
                                }
                            ]
                        }))
                    ]);

                    setRowData(schedules);

                    gridRef.current.api.closeToolPanel();

                    var elInfo = document.querySelector("#du-info");
                    elInfo.innerHTML = `${dispatchUnit.projectName} | ${dispatchUnit.dispatchUnitName} | ${dispatchUnit.quoteNumber} | ${dispatchUnit.locationName}`;
                    setLoading(false);

                    const dateExists = schedules.filter(s => {
                        if (!s.dates)
                            return false;

                        if (!Object.keys(s.dates).length)
                            return false;

                        return true;
                    }).length > 0;

                    setDisableTrimStartDateDefault(!dateExists);
                    setDisableTrimEndDateDefault(!dateExists);
                }
                catch (error) {
                    console.error({error});
                    setLoading(false);
                }
            }).catch((error) => {
                console.error({error});
                setLoading(false);
            }).finally(() => {

            });

    }, [staticColumnDefs]);

    const sideBar = useMemo(() => {
        return {
            toolPanels: [
                {
                    id: 'setupPanel',
                    labelDefault: 'Setup',
                    labelKey: 'setupPanel',
                    iconKey: 'fa-gear',
                    toolPanel: SetupPanel,
                    width: 280,
                    toolPanelParams: {
                        today: new Date(),
                        loadSchedule,
                        disableTrimStartDateDefault: disableTrimStartDateDefault,
                        disableTrimEndDateDefault: disableTrimEndDateDefault,
                    },
                },
            ],
            defaultToolPanel: 'setupPanel',
            position: "left"
        };
    }, []);

    return (
        <section className="main">
            {loading && <Loader />}
            <div className="ag-theme-alpine px-2" style={{ width: '100%', height: 'calc(100vh - 130px)' }}>
                <AgGridReact
                    ref={gridRef}
                    context={{ dispatchUnit, startDate, endDate, trimStartDate, trimEndDate, project }}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    suppressColumnVirtualisation={false}
                    suppressRowVirtualisation={false}
                    rowBuffer={20}
                    debounceVerticalScrollbar={false}
                    allowContextMenuWithControlKey={true}
                    suppressClipboardPaste={true}
                    headerHeight={70}
                    groupHeaderHeight={22}
                    rowHeight={22}
                    onGridReady={({ api }) => {
                        api.hideOverlay();
                    }}
                    readOnlyEdit={true}
                    getRowId={getRowId}
                    enableRangeSelection={true}
                    loadingOverlayComponent={loadingOverlayComponent}
                    sideBar={sideBar}
                    statusBar={statusBar}
                    tooltipShowDelay={0}
                    tooltipHideDelay={20000}
                    suppressDragLeaveHidesColumns={true}
                    suppressColumnMoveAnimation={true}
                    multiSortKey='ctrl'
                    copyHeadersToClipboard={true}
                    enableCellChangeFlash={true}
                    animateRows={true}
                />
            </div>
        </section>
    );
};
